import '../styles/nudgeMuiOverrides.css';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../lib/firebase';
import { signOut as signOutService } from '../services/authService';
import { captureReferralFromUrl, clearStoredReferralCode, getStoredReferralCode } from '../lib/referralCapture';
import { applyReferralCode, claimProductHuntOffer, ensureNudgeAccount, getNudgeAppState, getReferralSummary } from '../services/appStateService';

const STORAGE_KEY = 'nudge.frontend.v3';
const WORKSPACE_STORAGE_KEY = 'nudge.workspace';
const ORGANIZATION_STORAGE_KEY = 'nudge.organization';

export const defaultSettings = {
  mode: 'draft',
  lookbackDays: 30,
  automaticDiscovery: true,
  specificTargets: [],
  // New auto-send setups default to approval-first. Existing launch users that
  // predate this setting are preserved as "all" when server state is applied.
  autoSendPolicy: 'approval',
  allowedEmails: [],
  allowedDomains: [],
  monitorFutureThreads: true,
  attempts: 3,
  continueUntilReply: false,
  cadence: 'balanced',
  minDays: 3,
  maxDays: 5,
  smartTiming: true,
  varyNaturally: true,
  recipientLocalTime: true,
  avoidWeekends: true,
  stopOnReply: true,
  stopOnBounce: true,
  pauseOnOOO: true,
  blockedEmails: [],
  blockedDomains: [],
  customInstructions: '',
  removeNudgeBranding: false,
  includeThreadHistoryInDrafts: true,
  includeThreadAttachments: false,
  autoCreateDrafts: false,
  emailPrefs: {
    welcome: true,
    referralUpdates: true,
    scanResults: true,
    weeklyDigest: true,
    mailboxIssues: true,
    creditAlerts: true,
    productUpdates: false,
  },
};

function isProductHuntReferralCode(code = '') {
  return ['PRODUCTHUNT', 'PH', 'PRODUCTHUNT2026'].includes(String(code || '').trim().toUpperCase());
}

function captureInitialReferral() {
  if (typeof window === 'undefined') return '';
  const captured = captureReferralFromUrl();
  return captured.code || getStoredReferralCode();
}

function captureOrganizationFromUrl() {
  if (typeof window === 'undefined') return;
  const params = new URLSearchParams(window.location.search || '');
  const organizationId = String(params.get('org') || '').trim();
  const workspaceId = String(params.get('workspace') || '').trim();
  if (organizationId) localStorage.setItem(ORGANIZATION_STORAGE_KEY, organizationId);
  if (workspaceId) localStorage.setItem(WORKSPACE_STORAGE_KEY, workspaceId);
}

captureOrganizationFromUrl();

const initialReferralCode = captureInitialReferral();

const defaultState = {
  region: localStorage.getItem('nudge.region') || null,
  pendingReferralCode: initialReferralCode,
  authReady: false,
  initialAppLoaded: false,
  appLoading: false,
  backendError: '',
  user: null,
  admin: { isAdmin: false },
  organization: null,
  access: { role: 'owner', canManageOrganization: true, canManageBilling: true, workspaceIds: [] },
  company: null,
  subscription: null,
  referral: null,
  workspaces: [],
  allWorkspaces: [],
  defaultWorkspaceId: '',
  activeWorkspaceId: localStorage.getItem(WORKSPACE_STORAGE_KEY) || '',
  mailboxes: [],
  allMailboxes: [],
  onboardingComplete: false,
  active: false,
  settings: defaultSettings,
  analytics: {
    drafted: 0,
    sent: 0,
    replies: 0,
    creditsUsed: 0,
    creditsTotal: 100,
    bonusCredits: 0,
    creditsRemaining: 100,
    creditsExhausted: false,
    opportunityScansUsed: 0,
    opportunityScansTotal: 500,
    opportunityScansRemaining: 500,
    opportunitiesFound: 0,
    opportunitiesRejected: 0,
    watching: 0,
    pendingDrafts: 0,
    awaitingReply: 0,
    draftDeleted: 0,
    threadsReplied: 0,
    threadsCompleted: 0,
    threadsFailed: 0,
    nextFollowUpAt: null,
    lastScan: null,
    lastDraft: null,
    lastSent: null,
    lastReply: null,
    lastSync: null,
    recentActivity: [],
  },
};

function loadLocalDraft() {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    const pendingReferralCode = getStoredReferralCode() || saved.pendingReferralCode || initialReferralCode || '';
    return {
      ...defaultState,
      region: localStorage.getItem('nudge.region') || saved.region || null,
      pendingReferralCode,
      activeWorkspaceId: localStorage.getItem(WORKSPACE_STORAGE_KEY) || saved.activeWorkspaceId || '',
      settings: { ...defaultSettings, ...(saved.settings || {}) },
    };
  } catch {
    return defaultState;
  }
}

function persistDraft(state) {
  localStorage.setItem(
    STORAGE_KEY,
    JSON.stringify({
      region: state.region,
      pendingReferralCode: state.pendingReferralCode,
      activeWorkspaceId: state.activeWorkspaceId,
      organizationId: state.organization?.id || localStorage.getItem(ORGANIZATION_STORAGE_KEY) || '',
      settings: state.settings,
    }),
  );
}

const AppContext = createContext(null);

export function AppProvider({ children }) {
  const [state, setState] = useState(loadLocalDraft);

  const applyServerState = (server) => {
    const nudge = server?.nudge || {};
    setState((current) => {
      const serverRegion = server?.company?.region || server?.dataRegion || current.region;
      const organization = server?.organization || current.organization || null;
      if (organization?.id) localStorage.setItem(ORGANIZATION_STORAGE_KEY, organization.id);
      const workspaces = Array.isArray(server?.workspaces) ? server.workspaces : [];
      const allWorkspaces = Array.isArray(server?.allWorkspaces) ? server.allWorkspaces : workspaces;
      const defaultWorkspaceId = server?.defaultWorkspaceId || workspaces.find((item) => item.isDefault)?.id || workspaces[0]?.id || '';
      const storedWorkspaceId = localStorage.getItem(WORKSPACE_STORAGE_KEY) || current.activeWorkspaceId || '';
      const activeWorkspaceId = workspaces.some((item) => item.id === storedWorkspaceId)
        ? storedWorkspaceId
        : defaultWorkspaceId;
      const activeWorkspace = workspaces.find((item) => item.id === activeWorkspaceId) || null;
      const effectiveSettings = activeWorkspace?.settings || nudge.settings || {};

      // Preserve historical behavior for already-onboarded accounts that were
      // created before autoSendPolicy existed. New accounts start approval-first.
      const effectiveAutoSendPolicy = Object.prototype.hasOwnProperty.call(effectiveSettings, 'autoSendPolicy')
        ? effectiveSettings.autoSendPolicy
        : (nudge.onboardingComplete ? 'all' : 'approval');

      if (activeWorkspaceId) localStorage.setItem(WORKSPACE_STORAGE_KEY, activeWorkspaceId);

      const next = {
        ...current,
        region: serverRegion,
        backendError: '',
        user: server?.user || null,
        admin: server?.admin || { isAdmin: false },
        organization,
        access: server?.access || current.access,
        company: server?.company || null,
        subscription: server?.subscription || null,
        referral: server?.referral || current.referral || null,
        workspaces,
        allWorkspaces,
        defaultWorkspaceId,
        activeWorkspaceId,
        mailboxes: server?.mailboxes || [],
        allMailboxes: server?.allMailboxes || server?.mailboxes || [],
        onboardingComplete: Boolean(nudge.onboardingComplete),
        active: Boolean(nudge.active),
        settings: {
          ...defaultSettings,
          autoSendPolicy: effectiveAutoSendPolicy,
          ...effectiveSettings,
        },
        analytics: {
          ...defaultState.analytics,
          ...(nudge.usage || {}),
        },
      };
      persistDraft(next);
      return next;
    });
  };

  const applyStoredReferralIfNeeded = async () => {
    const code = getStoredReferralCode();
    if (!code || !auth?.currentUser) return null;
    try {
      let result;

      if (isProductHuntReferralCode(code)) {
        setState((current) => {
          const next = {
            ...current,
            pendingReferralCode: code,
          };
          persistDraft(next);
          return next;
        });
      
        return { ok: true, launchCode: code };
      }

      result = await applyReferralCode({ code, source: 'url' });
      clearStoredReferralCode();

      setState((current) => {
        const next = { ...current, pendingReferralCode: '', referral: result?.referral || current.referral };
        persistDraft(next);
        return next;
      });

      return result;
    } catch (error) {
      // Keep the code locally if the account is not ready yet. Clear it only for permanent/self-referral style failures.
      if (String(error?.message || '').toLowerCase().includes('own referral')) clearStoredReferralCode();
      return null;
    }
  };

  const refreshReferral = async () => {
    if (!auth?.currentUser) return null;
    const result = await getReferralSummary();
    setState((current) => {
      const next = { ...current, referral: result?.referral || current.referral };
      persistDraft(next);
      return next;
    });
    return result;
  };

  const refreshAppState = async ({ ensure = false, initial = false } = {}) => {
    if (!auth?.currentUser) return null;
    const startedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
    setState((current) => ({
      ...current,
      appLoading: true,
      backendError: '',
      ...(initial ? { initialAppLoaded: false } : {}),
    }));
    try {
      if (ensure) await ensureNudgeAccount();
      await applyStoredReferralIfNeeded();
      const server = await getNudgeAppState();
      if (server?.needsAccountCreation) {
        await ensureNudgeAccount();
        await applyStoredReferralIfNeeded();
        const refreshed = await getNudgeAppState();
        applyServerState(refreshed);
        if (initial) {
          const endedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
          console.info('Nudge initial account state loaded after account creation', {
            ms: Math.round(endedAt - startedAt),
          });
        }
        return refreshed;
      }
      applyServerState(server);
      if (initial) {
        const endedAt = typeof performance !== 'undefined' ? performance.now() : Date.now();
        console.info('Nudge initial account state loaded', {
          ms: Math.round(endedAt - startedAt),
          ensuredBeforeLoad: Boolean(ensure),
        });
      }
      return server;
    } catch (error) {
      setState((current) => ({
        ...current,
        backendError: error?.message || 'Could not load your Nudge account.',
      }));
      throw error;
    } finally {
      setState((current) => ({
        ...current,
        appLoading: false,
        ...(initial ? { initialAppLoaded: true } : {}),
      }));
    }
  };

  useEffect(() => {
    captureReferralFromUrl();
    setState((current) => {
      const pendingReferralCode = getStoredReferralCode() || current.pendingReferralCode || '';
      const next = { ...current, pendingReferralCode };
      persistDraft(next);
      return next;
    });
  }, []);

  useEffect(() => {
    if (!auth) {
      setState((current) => ({
        ...current,
        authReady: true,
        initialAppLoaded: true,
        appLoading: false,
        backendError: 'Firebase is not configured for this environment.',
      }));
      return undefined;
    }

    return onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setState((current) => ({
          ...current,
          authReady: true,
          initialAppLoaded: true,
          appLoading: false,
          user: null,
          admin: { isAdmin: false },
          organization: null,
          access: { role: 'owner', canManageOrganization: true, canManageBilling: true, workspaceIds: [] },
          company: null,
          subscription: null,
          referral: null,
          workspaces: [],
          allWorkspaces: [],
          defaultWorkspaceId: '',
          activeWorkspaceId: '',
          mailboxes: [],
          allMailboxes: [],
          onboardingComplete: false,
          active: false,
          analytics: defaultState.analytics,
        }));
        return;
      }

      setState((current) => ({
        ...current,
        authReady: true,
        initialAppLoaded: false,
        appLoading: true,
        user: {
          id: firebaseUser.uid,
          email: firebaseUser.email || '',
          displayName: firebaseUser.displayName || '',
        },
      }));

      try {
        await refreshAppState({ initial: true });
      } catch {
        // Error is surfaced through state.backendError.
      }
    });
  }, []);

  const update = (patch) => {
    setState((current) => {
      const next = { ...current, ...patch };
      if (patch.region) localStorage.setItem('nudge.region', patch.region);
      persistDraft(next);
      return next;
    });
  };

  const updateSettings = (patch) => {
    setState((current) => {
      const settings = { ...current.settings, ...patch };
      const workspaces = (current.workspaces || []).map((workspace) => (
        workspace.id === current.activeWorkspaceId
          ? { ...workspace, settings }
          : workspace
      ));
      const next = { ...current, settings, workspaces };
      persistDraft(next);
      return next;
    });
  };

  const setActiveWorkspace = (workspaceId) => {
    setState((current) => {
      const workspace = (current.workspaces || []).find((item) => item.id === workspaceId);
      if (!workspace) return current;
      localStorage.setItem(WORKSPACE_STORAGE_KEY, workspace.id);
      const next = {
        ...current,
        activeWorkspaceId: workspace.id,
        settings: { ...defaultSettings, ...(workspace.settings || {}) },
      };
      persistDraft(next);
      return next;
    });
  };

  const logout = async () => {
    localStorage.removeItem(WORKSPACE_STORAGE_KEY);
    localStorage.removeItem(ORGANIZATION_STORAGE_KEY);
    await signOutService();
  };

  const chooseRegionAgain = async () => {
    await signOutService();
    localStorage.removeItem('nudge.region');
    localStorage.removeItem(WORKSPACE_STORAGE_KEY);
    localStorage.removeItem(ORGANIZATION_STORAGE_KEY);
    // Keep pending referral code. Region selection is intentionally first, but referrals must survive region choice.
    setState((current) => ({
      ...current,
      region: null,
      user: null,
      admin: { isAdmin: false },
      organization: null,
      access: { role: 'owner', canManageOrganization: true, canManageBilling: true, workspaceIds: [] },
      company: null,
      subscription: null,
      referral: null,
      workspaces: [],
      allWorkspaces: [],
      defaultWorkspaceId: '',
      activeWorkspaceId: '',
      mailboxes: [],
      allMailboxes: [],
      onboardingComplete: false,
      active: false,
      analytics: defaultState.analytics,
    }));
  };

  const reset = () => {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem('nudge.region');
    localStorage.removeItem(WORKSPACE_STORAGE_KEY);
    localStorage.removeItem(ORGANIZATION_STORAGE_KEY);
    clearStoredReferralCode();
    setState(defaultState);
  };

  const value = useMemo(
    () => ({
      state,
      update,
      updateSettings,
      setActiveWorkspace,
      refreshAppState,
      refreshReferral,
      applyServerState,
      logout,
      chooseRegionAgain,
      reset,
    }),
    [state],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useApp() {
  const context = useContext(AppContext);
  if (!context) throw new Error('useApp must be used inside AppProvider');
  return context;
}

